import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '@components/atoms/ui-kit/button.tsx';
import {
  ResetPasswordRequest,
  useResetPassword
} from '@utils/api/users/use-reset-password.tsx';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { Mail } from 'lucide-react';
import Input from '@components/atoms/ui-kit/input.tsx';
import PublicLogo from '@components/shared/public-logo.tsx';
import { useMemo } from 'react';
import { Card } from '@components/atoms/ui-kit/card';
import { Heading, Text } from '@components/atoms/ui-kit/text';

type FormValues = {
  email: string;
  reset_key: string;
  password: string;
  confirmPassword: string;
};

export default function ChangePassword() {
  const { t } = useTranslation();
  const mutation = useResetPassword();

  const schema = useMemo(
    () =>
      yup.object({
        email: yup
          .string()
          .required(t('email-required'))
          .email(t('email-is-invalid')),
        reset_key: yup.string().required(t('reset-key-required')),
        password: yup.string().required(t('new-password-is-required')),
        confirmPassword: yup
          .string()
          .oneOf([yup.ref('password')], t('passwords-must-match'))
          .required(t('confirm-password-is-required'))
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: 'onBlur'
  });

  const passwordValue = watch('password') || '';

  const onSubmit = (data: FormValues) => {
    mutation
      .mutateAsync({
        email: data.email,
        password: data.password,
        reset_key: data.reset_key
      } as ResetPasswordRequest)
      .then(response => {
        if (response.success) {
          toast.success(t('password-reset-successfully'));
        } else {
          toast.error(t('failed-to-change-password-please-try-again'));
        }
      })
      .catch(error => {
        const axiosError = error as AxiosError<BaseError>;
        const errorMessage =
          axiosError.response?.data?.error_description ||
          axiosError.response?.data?.detail ||
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          t('failed-to-change-password-please-try-again');
        toast.error(errorMessage);
      });
  };

  const InlineError = ({ id, msg }: { id: string; msg?: string }) =>
    msg ? (
      <Text
        as="p"
        id={id}
        size="xs"
        weight="medium"
        tone="danger"
        className="mt-1"
      >
        {msg}
      </Text>
    ) : null;

  const PasswordCriteria = ({ password }: { password: string }) => {
    const rules = [
      {
        label: t('password-must-be-at-least-12-characters-long'),
        ok: password.length >= 12
      },
      {
        label: t('password-must-have-at-least-one-uppercase-letter'),
        ok: /[A-Z]/.test(password)
      },
      {
        label: t('password-must-have-at-least-one-lowercase-letter'),
        ok: /[a-z]/.test(password)
      },
      {
        label: t('password-must-have-at-least-one-number'),
        ok: /\d/.test(password)
      }
    ];
    return (
      <ul className="mt-2 space-y-1.5">
        {rules.map((r, i) => (
          <li key={i}>
            <Text as="span" size="sm" tone={r.ok ? 'brand' : 'muted'}>
              {r.ok ? '✓' : '○'} {r.label}
            </Text>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="bg-app min-h-screen w-full flex items-center justify-center px-4 py-10">
      <Card className="w-full max-w-[520px]">
        <div className="flex flex-col items-center gap-3 mb-2">
          <PublicLogo className="h-auto w-[11rem]" />
          <Heading level={1} size="2xl" className="leading-8 tracking-tight">
            {t('change-password')}
          </Heading>
          <Text as="p" size="sm" className="text-center">
            {t('password-change-description')}
          </Text>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Email */}
          <div className="mb-4">
            <Input
              id="email"
              type="email"
              autoComplete="email"
              label={t('email')}
              className="block w-full"
              aria-invalid={!!errors.email}
              aria-describedby="email-error"
              {...register('email')}
            />
            <InlineError id="email-error" msg={errors.email?.message} />
          </div>

          {/* Reset Key */}
          <div className="mb-4">
            <Input
              id="reset_key"
              type="text"
              autoComplete="one-time-code"
              label={t('reset-key-received-by-email')}
              className="block w-full"
              aria-invalid={!!errors.reset_key}
              aria-describedby="reset-key-error"
              {...register('reset_key')}
            />
            <InlineError id="reset-key-error" msg={errors.reset_key?.message} />
          </div>

          {/* New Password */}
          <div className="mb-4">
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              label={t('new-password')}
              className="block w-full"
              aria-invalid={!!errors.password}
              aria-describedby="password-error"
              {...register('password')}
            />
            <InlineError id="password-error" msg={errors.password?.message} />
            {passwordValue.length > 0 && (
              <PasswordCriteria password={passwordValue} />
            )}
          </div>

          {/* Confirm Password */}
          <div className="mb-6">
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              label={t('confirm-password')}
              className="block w-full"
              aria-invalid={!!errors.confirmPassword}
              aria-describedby="confirm-password-error"
              {...register('confirmPassword')}
            />
            <InlineError
              id="confirm-password-error"
              msg={errors.confirmPassword?.message}
            />
          </div>

          {/* Actions */}
          <Button
            type="submit"
            label={
              isSubmitting || mutation.isLoading
                ? t('submitting')
                : t('password-reset')
            }
            disabled={isSubmitting || mutation.isLoading}
            className="w-full"
          />
        </form>

        {/* Divider + Support */}
        <div className="relative mt-6">
          <div
            className="absolute inset-0 flex items-center"
            aria-hidden="true"
          >
            <div className="w-full border-t border-hairline" />
          </div>
          <div className="relative flex justify-center font-medium leading-6">
            <Text as="span" size="sm" className="px-4 bg-app-soft">
              {t('need-help')}
            </Text>
          </div>
        </div>

        <div className="mt-3">
          <Button
            label={t('scriptix-support')}
            icon={<Mail width={14} />}
            onClick={() => (window.location.href = 'mailto:info@scriptix.io')}
            variant="gray"
            className="w-full"
          />
        </div>
      </Card>
    </div>
  );
}
